import React, { useState, useMemo } from 'react';
import MaterialTable from 'material-table';
import { Fab, withStyles, Typography, Tooltip, IconButton, 
         } from '@material-ui/core';
import * as actions from './store/orderActions';
import * as action from '../../np-core/Auth/store/authActions';
import { connect } from 'react-redux';
import { Add, Delete, Refresh } from '@material-ui/icons';
import OrderDelete from './OrderDelete'
import { hasPermission, BackButton, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import { CsvBuilder } from 'filefy';
import Toolbar from '../../sc-utilities/Toolbar'

const styles = theme => ({
    "@global": {
        "[class*='buttonsCustomer']": {
            margin: theme.spacing(),
            fontSize: '13px',
            width: '100%',
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                color: theme.palette.buttonText.default
            },
        },
        "[class*='paletteButtons']": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.buttonText.default,
            margin: theme.spacing(),
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        }
    },
    fab: {
        textTransform: "none",
        width: '50%',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    }
});
let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const OrderList = props => {
    const { classes, translations, goToCreateOrder, saveColumnsFilter, activeExternalServiceAvailable, activeCustomer, permissions, tableColumns, fetchOrderDataCount, orders, fetchOrderData, tableRef } = props;
    const [deleteModalState, setDeleteModalState] = useState({
        open: false,
        orderID: null,
    });

    let tableActions = [
        {
            tooltip: `${translations['remove_all_selected'] || 'Remove all selected'} ${translations["orders"] || "Orders"}`,
            onClick: (data) => handleBulkDelete(data.map(order => order.OrderID)),
            icon: <Delete />
        }
    ]

    const customExport = (columns, ordersList) => {
        if (columns && columns[0].field === 'Action') {
            columns.shift()
        }
        if (!activeExternalServiceAvailable) {
            tableColumns.splice(1, 1);
        }

        const data = ordersList.map(order => {
            let returnObj = [
                order.OrderInventoryID ? order.OrderInventoryID : translations['short_not_assigned'] || 'n/a',
                order.PaletteExternalInfo ? order.PaletteExternalInfo.map((externalInfo) => { return `${externalInfo.ExternalServiceType} - ${externalInfo.ExternalID}` }) : translations['short_not_assigned'] || 'n/a',
                order.Name ? order.Name : translations['short_not_assigned'] || 'n/a',
                order.Description ? order.Description : translations['short_not_assigned'] || 'n/a',
                order.ReportInformation ? order.ReportInformation : translations['short_not_assigned'] || 'n/a',
                order.FilePattern && order.FilePattern.Name ? order.FilePattern.Name : translations['short_not_assigned'] || 'n/a',
                order.ErasureReportSignature ? translations['yes'] || 'Yes' : translations['no'] || 'No',
                order.Deleted ? translations['yes'] || 'Yes' : translations['no'] || 'No',
            ];
            if (!activeExternalServiceAvailable) {
                returnObj.splice(1, 1);
            }
            return returnObj;
        });
        const builder = new CsvBuilder(`securaze_order_${Math.floor(Date.now() / 1000)}.csv`);
        builder
            .setDelimeter('|')
            .setColumns(columns.map(columnDef => columnDef.title))
            .addRows(data)
            .exportFile();
    };
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['order_delete']);
    const deletePermission = hasPermission(permissions, 'order_delete');

    const fetching = orders.fetching;
    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []); const [tableOptions, setTableOptions] = useState({
            filtering: true,
            sorting: true,
            selection: selectionPermissions,
            selectionProps: rowData => {
                let disabled = false;
                if (!deletePermission || (deletePermission && rowData.TransportCollies.length > 0)) {
                    disabled = true;
                }
                rowData.tableData.disabled = disabled;
                return disabled ? {
                    disabled: true,
                    checked: false
                } : {}
            },
            search: false,
            thirdSortClick: false,
            minBodyHeight: window.innerHeight - 197 < 300 ? 300 : window.innerHeight - 197,
            maxBodyHeight: window.innerHeight - 197,
            pageSizeOptions: rowsPageSizeInit,
            exportButton: false,
            exportCsv: customExport,
            rowStyle: (_rowData, index) => {
                return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
            },
            columnsButton: true,
            pageSize: pageSize,
            emptyRowsWhenPaging: false
        });

    const handleBulkDelete = orderIDS => {
        if (!orderIDS) {
            setDeleteModalState({ open: false, orderIDs: [] });
            return;
        }
        setDeleteModalState({ open: true, orderIDs: orderIDS });
    };

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        if (query?.orderBy?.field) {
            query['orders'] = [{
                field: query?.orderBy?.field, direction: query?.orderDirection
            }]
        }
        query['ActiveCustomerID'] = activeCustomer;
        query['q'] = q;
        let count = await fetchOrderDataCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await fetchOrderData(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }


    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`Order`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomer })
        return newColumnState
    };

    const handleCloseDanger = async () => {
        await props.onBulkDeleteOrders({ OrderIDs: deleteModalState.orderIDs, ActiveCustomerID: activeCustomer });
        setDeleteModalState({ open: false, orderIDs: [] });
        tableRef.current && tableRef.current.onQueryChange()
    };

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>
                    {translations["orders"] || "Orders"}
                </Typography>
                {hasPermission(permissions, "order_create") ? (<Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={goToCreateOrder}

                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab>) : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    return (<>
        <MaterialTable
            tableRef={tableRef}
            title={tableTitle()}
            columns={columnObject}
            data={fetchTableData}
            options={tableOptions}
            components={{
                Toolbar: props => (
                    <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                )
            }}
            onChangeRowsPerPage={(size) => {
                pageSize = size
            }}
            isLoading={fetching}
            localization={localization(translations)}
            onChangeColumnHidden={columnHidden}
            onChangePage={() => { if (tableRef?.current?.tableContainerDiv?.current) { tableRef.current.tableContainerDiv.current.scrollTop = 0 } }}
        />
        {hasPermission(permissions, "order_delete") ? (
            <OrderDelete
                activeCustomer={activeCustomer}
                entityName={translations["order"] || "Order"}
                translations={translations}
                handleDelete={handleBulkDelete}
                handleCloseDanger={handleCloseDanger}
                deleteModalState={deleteModalState}
            />
        ) : null}        
    </>);
};

const mapDispatchToProps = dispatch => {
    return {
        fetchOrderDataCount: filter => dispatch(actions.fetchOrderCount(filter)),
        fetchOrderData: filter => dispatch(actions.fetchOrder(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onDeleteOrder: order => dispatch(actions.deleteOrder(order)),
        onBulkDeleteOrders: order => dispatch(actions.bulkDeleteOrders(order)),
    };
};

const mapStateToProps = state => {
    return {
        permissions: state?.core?.login?.permissions || [],
        activeCustomer: state?.core?.login?.activeCustomer?.CustomerID ?? null,
        orders: state.order.orders,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        activeExternalServiceAvailable: state.core.login.activeExternalServiceAvailable,
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(OrderList));